// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Maps the flat AEM alarm-api record (`AlarmApiRecord`) into the richer UI-mock
 * `Alarm` domain model that `AlarmDetail` renders. AEM carries far fewer fields
 * than the mockup model, so absent fields are filled with safe defaults and the
 * detail page's optional sections (telemetry, causes, audit trail) simply stay
 * empty. When the alarm's linked SOP is resolved from the SOP Factory, its
 * procedure steps populate the "Response procedure" section.
 */

import type { Alarm, Severity } from '../types/alarm';
import type { AlarmApiRecord, AlarmApiStatus } from '../types/alarmApi';
import type { Artifact, SopStep } from '../types/sopFactory';

/** Human-friendly status chip label for an AEM status. */
function statusChip(status: AlarmApiStatus | null): string | undefined {
  if (!status) return undefined;
  return status.charAt(0).toUpperCase() + status.slice(1);
}

/** Render one SOP step as a single instruction line. */
function stepLine(step: SopStep): string {
  const instruction = (step.instruction ?? '').trim();
  if (step.verb && instruction) return `${step.verb} — ${instruction}`;
  return instruction || (step.verb ?? '');
}

/**
 * Build the `Alarm` shape from an AEM record plus (optionally) the SOP artifact
 * linked via `sop_ids[0]`. `sop` is the resolved SOP Factory artifact, if any.
 */
export function alarmApiRecordToAlarm(record: AlarmApiRecord, sop?: Artifact | null): Alarm {
  const sopId = record.sop_ids?.[0] ?? '';
  const steps: string[] = (sop?.payload?.steps ?? [])
    .map(stepLine)
    .filter((s): s is string => Boolean(s));

  const durationHours = sop?.payload?.estimated_duration_hours;
  const labor = typeof durationHours === 'number' ? `~${durationHours}h` : undefined;

  const asset = record.equipment_key ?? record.equipment_id ?? record.alarm_code ?? 'Unknown asset';
  const issue = sop?.title
    ? `${record.alarm_code ?? 'Alarm'} — ${sop.title}`
    : (record.alarm_code ?? 'Alarm');

  return {
    id: record.alarm_code ?? '',
    ruleId: record.alarm_rule_ids?.[0],
    point: record.point_key ?? undefined,
    // AEM has no ISA severity; default to P3 until the API supplies one.
    sev: 'P3' as Severity,
    time: record.acknowledged_at ?? '',
    asset,
    assetId: record.equipment_id ?? undefined,
    location: record.site_key ?? '',
    siteId: record.site_id ?? '',
    orgId: record.organization_id ?? undefined,
    group: record.equipment_type ?? undefined,
    src: record.point_key ?? undefined,
    statusChip: statusChip(record.alarm_status),
    status: record.alarm_status ?? undefined,
    summary: sop?.title ?? record.equipment_key ?? record.alarm_code ?? '',
    issue,
    diagnosis: sop?.payload?.description_of_work ?? '',
    confidence: 'Medium',
    sopId,
    sopVersion: sop?.version,
    labor,
    steps,
    telemetry: [],
    auditTrail: [],
    owner: record.assigned_to ?? undefined,
    needsAck: record.alarm_status === 'open' || record.alarm_status === 'active',
    closed: record.alarm_status === 'closed' || record.alarm_status === 'cleared',
  };
}
